import { type FC, type JSX, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { IconDelivery, IconEuro, IconPrescription } from '@redteclab/icons'

import { Dialog, DialogContent, DialogTitle, Link, Notification } from 'base-ui'

import { useGlobalConfigContext } from '../../../global-config'
import { NextLink } from '../../../next-link'
import {
  urlResolverGetFreeEnvelopePdf,
  urlResolverGetRezeptbonusLandingPage,
} from '../../../url-handling'

const freeEnvelopePdfLink = (text: ReactNode[]): JSX.Element => (
  <Link href={urlResolverGetFreeEnvelopePdf()} rel="noreferrer" target="_blank">
    {text}
  </Link>
)

export const ProductPrivatePrescriptionBonusDialog: FC<{
  isOpen: boolean
  onClose: () => void
}> = ({ isOpen, onClose }) => {
  const globalConfig = useGlobalConfigContext()
  const intl = useIntl()

  const footnotesAriaLabel = intl.formatMessage({
    id: 'footnotes.ariaLabel',
  })

  const prescriptionLink = (text: ReactNode[]): JSX.Element => (
    <Link
      href={urlResolverGetRezeptbonusLandingPage(globalConfig)}
      slots={{ root: NextLink }}
    >
      {text}
    </Link>
  )

  return (
    <Dialog
      data-qa-id="prescription-bonus-dialog"
      onClose={onClose}
      open={isOpen}
    >
      <DialogTitle onClose={onClose}>
        <FormattedMessage id="prescriptionBonus.modal.title" />
        <sup>{1}</sup>
      </DialogTitle>
      <DialogContent>
        <div className="prose">
          <p>
            <FormattedMessage
              id="prescriptionBonus.modal.descriptionText"
              values={{
                redeemRecipeLink: freeEnvelopePdfLink,
              }}
            />
          </p>
          <ul className="not-prose mb-3 flex flex-col gap-4 text-s font-medium">
            <li>
              <IconEuro className="not-prose mr-3 inline size-6" />
              <FormattedMessage id="prescriptionBonus.modal.benefit1" />
            </li>
            <li>
              <IconPrescription className="not-prose mr-3 inline size-6" />
              <FormattedMessage id="prescriptionBonus.modal.benefit2" />
            </li>
            <li>
              <IconDelivery className="not-prose mr-3 inline size-6" />
              <FormattedMessage id="prescriptionBonus.modal.benefit3" />
            </li>
          </ul>
          <p>
            <FormattedMessage
              id="prescriptionBonus.modal.privatePatient.descriptionText"
              values={{
                redeemRecipeLink: prescriptionLink,
              }}
            />
          </p>
        </div>
        <Notification className="mt-4" severity="warning">
          <FormattedMessage id="prescriptionBonus.modal.notice" />
        </Notification>
        <section
          aria-label={footnotesAriaLabel}
          className="mt-6 flex flex-row items-start text-dark-primary-strong"
        >
          <span className="mr-1">{1}</span>
          <p>
            <FormattedMessage id="footnotes.erx.bonus" />
          </p>
        </section>
      </DialogContent>
    </Dialog>
  )
}
